<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import type { Bet } from '@nsftx/seven-client-retail-vue-integrator'
import { SCButton, ButtonVariant } from '@nsftx/seven-components'
import { getBetDetails as _getBetDetails, useBetslip } from '@nsftx/seven-client-retail-vue-sgp'
import { useTicketCheckStore } from '@/stores/ticket-check/ticketCheckStore'
import ticketCheckService from '@/modules/ticket-check/ticketCheckService'
import { handleTicketPayout } from '@/modules/ticket-payout/ticketPayoutService'
import STTicketCheckDetailsBetSelections from './STTicketCheckDetailsBetSelections.vue'
import STTicketCheckDetailsBetSummary from './STTicketCheckDetailsBetSummary.vue'

interface TicketCheckDetailsBetProps {
  bet: Bet
}

const { bet } = defineProps<TicketCheckDetailsBetProps>()

const { betslipTimestamp } = useBetslip()
const { ticketId } = storeToRefs(useTicketCheckStore())

const betslipDate = computed(() =>
  ticketCheckService.getFormattedDate(betslipTimestamp.value || '')
)
const getBetDetails = computed(() => (bet: Bet) => _getBetDetails(bet))
</script>

<template>
  <div class="st-ticket-check-details-bet sc-pt-5">
    <p class="st-ticket-check-details-bet__header sc-text-h4">
      <span>{{ getBetDetails(bet).type }}</span>
      <span class="sc-mx-5">|</span>
      <span class="st-ticket-check-details-bet__header__status">{{
        getBetDetails(bet).status
      }}</span>
    </p>
    <p class="st-ticket-check-details-bet__subheader sc-mb-5">
      <span>{{ $t('betslip_id') }}: </span
      ><span class="st-ticket-check-details-bet__subheader__code">
        {{ ticketId }}
      </span>
      <span class="sc-mx-3">|</span>
      <span>{{ betslipDate }}</span>
    </p>

    <div class="st-d-flex-column sc-mb-6">
      <STTicketCheckDetailsBetSelections v-if="bet.selections" :bet-selections="bet.selections" />
      <STTicketCheckDetailsBetSummary :bet />
    </div>

    <div class="st-ticket-check-details-bet__actions">
      <SCButton
        v-if="getBetDetails(bet).isStatusWon"
        :variant="ButtonVariant.Primary"
        data-cy="ticketPayoutButton"
        @click="() => handleTicketPayout(ticketId, bet)"
      >
        {{ $t('common_payout') }}
      </SCButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.st-ticket-check-details-bet {
  &__header {
    margin-bottom: 1.25rem;

    &__status {
      display: inline-block;
      color: #e2bf41;
      text-transform: lowercase;

      &::first-letter {
        text-transform: uppercase;
      }
    }
  }

  &__subheader {
    font-size: 0.875rem;

    span {
      &:not(&.st-ticket-check-details-bet__subheader__code) {
        opacity: 0.6;
        font-weight: normal;
      }
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }
}
</style>
